import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TopNavigation } from './TopNavigation';
import { Sidebar } from './Sidebar';
import { Toaster } from '@/components/ui/sonner';
import QuickAddFAB from '@/components/QuickAddFAB';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  return (
    <div data-testid="main-layout" className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <TopNavigation 
        onMenuToggle={toggleSidebar} 
        isMobile={isMobile}
        sidebarOpen={sidebarOpen}
      />

      {/* Main Content Area */}
      <div className="flex relative">
        {/* Mobile Overlay */}
        {isMobile && sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - Hidden on mobile unless toggled */}
        <div className={cn(
          "fixed lg:relative z-50 lg:z-auto transition-transform duration-300 ease-in-out",
          "lg:translate-x-0",
          isMobile && !sidebarOpen && "-translate-x-full",
          isMobile && sidebarOpen && "translate-x-0"
        )}>
          <Sidebar onClose={() => setSidebarOpen(false)} isMobile={isMobile} />
        </div>

        {/* Page Content */}
        <main 
          data-testid="main-content" 
          className={cn(
            "flex-1 p-4 md:p-6 overflow-auto h-[calc(100vh-4rem)]",
            "w-full lg:w-auto"
          )}
        >
          <Outlet />
        </main>
      </div>
      
      {/* Quick Add FAB - Available on all pages */}
      <QuickAddFAB />
      
      <Toaster position="top-right" />
    </div>
  );
}
